<!--
  The app-wide blocking loader. Three things were wrong here and all three end in the same symptom — the
  backdrop covers the screen and the indicator is nowhere, which reads as a frozen app rather than as a slow
  one. See `fullscreen-loader.component.spec.ts`; all three predate #602.

  1. `absolute`, not `fixed`. With no positioned ancestor both layers resolved against the initial containing
     block (the whole document), so on a scrolled page the backdrop still covered the viewport while the
     indicator's `top-1/2` sat at half the PAGE height, far above or below it.
  2. `z-50` put the loader level with the office navbar (`z-50`) and under the context drawer (`z-[890]`), so
     the app's own chrome painted over the thing that is meant to block it.
  3. The spinner's `<g>` carried `fill-none`. `fill` is an INHERITED property and none of the paths declared a
     fill or a stroke of their own, so the whole glyph inherited `fill: none` and drew nothing — in every
     engine, not only WebKit. Exactly Bug #256's shape, one component over.

  Since #775 the indicator is a `thinking-orbs` orb on a 2D canvas instead of that SVG wheel, so (3) now has a
  canvas shape rather than an SVG one — a canvas that is present and correctly sized but never painted is the
  same defect and the same symptom. The spec pins it by reading pixels back, not by reading classes.
  Bug #218's rule survives the move too and is enforced inside `ThinkingOrbComponent`: the CSS size is set on
  the <canvas> itself, because a canvas with no CSS box falls back to its intrinsic 300x150 and one sized only
  by its parent does not shrink-wrap.
-->
<!-- `show()`, not `show`: a signal input is a FUNCTION, and a function reference is always truthy — written
     without the call the loader is permanently on screen and the app is unusable. -->
@if (show()) {
  <div data-test="loader-backdrop" aria-hidden="true"
       class="fixed inset-0 z-[1000] bg-gray-500 opacity-75"></div>
  <div data-test="loader-wheel" role="status" class="fixed inset-0 z-[1001]">
    <!-- `aria-hidden` on the orb, NOT on the layer: the `role="status"` region is already named by the
         `sr-only` text below, so a labelled `role="img"` in here would make a screen reader announce the same
         thing twice. The orb keeps its own label for every other caller; only this one has a better name. -->
    <div aria-hidden="true"
         class="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2">
      <!-- `theme="dark"` is the SUBSTRATE, not the ink: it means "this orb sits on a dark background", which
           is the direction the ink ramp runs in. The backdrop is a dimmed `bg-gray-500` sheet, so the near
           marks resolve light and the far ones dark.

           96px, not the engine's tuned 64. At 64 every one of the 566 marks is sub-pixel and 41% of them are
           under half a pixel, so the orb reads as an anti-aliased smudge rather than as the dotted texture it
           is. 96 with `compensateDensity` (the default) puts the median mark at 0.82 CSS px and holds ink
           coverage at 14.0%, i.e. the 64px design geometrically enlarged rather than thinned out. Turning the
           compensation off would make it bigger AND fainter — 10.2% coverage — which is the opposite of the
           point. 128 resolves the texture further still and was measured, but at 390px phone width it takes a
           third of the viewport.

           `tint` takes a THEME TOKEN, never a hex: the colour is decided in `tailwind.config.js`, which maps
           `theme-primary` onto its scale. `theme-primary-700` is the same token the SVG wheel this replaced
           painted with, so the swap keeps the brand colour instead of dropping it — and on the measured
           composited backdrop it carries the strongest marks better than white does. -->
      <app-thinking-orb state="composing" [size]="96" theme="dark" tint="theme-primary-700"/>
    </div>
    <!-- A blocking overlay with no accessible name announces nothing at all: a screen-reader user gets silence
         where a sighted user gets an indicator. -->
    <span class="sr-only">Wird geladen …</span>
  </div>
}
